import { AlertTriangle } from 'lucide-react'
import { Link } from 'react-router'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { formatPlatformList, usePlatformSyncIssues } from '@/lib/platform-sync-status'

interface Props {
  /** Platforms with at least one account the user has picked to publish to. */
  selectedPlatforms: PlatformType[]
}

/**
 * Banner shown in the workspace preview modal when a platform the user has
 * actively selected an account for has a confirmed reauth issue. We narrow
 * to `needs_reauth` only — `missing_scopes` affects analytics sync, not
 * publishing, and `rate_limited` / `error` are transient sync-side issues
 * that don't predict a publish failure.
 *
 * Scoped to `selectedPlatforms` (not `activePlatforms`) so we don't warn
 * about LinkedIn when the user is publishing to Instagram only, or when the
 * user has the platform tab visible but hasn't picked an account yet.
 *
 * Never blocks the publish button — the postFailed email is the safety net
 * if the warning is missing (because the cron hasn't observed the failure
 * yet) or wrong (transient).
 */
export const WorkspacePreviewSyncStatus = ({ selectedPlatforms }: Props) => {
  const issues = usePlatformSyncIssues()
  const affected = issues
    .filter((i) => i.kind === 'needs_reauth' && selectedPlatforms.includes(i.platform))
    .map((i) => i.platform)

  if (affected.length === 0) return null

  const isPlural = affected.length > 1

  return (
    <div className="mx-auto w-full max-w-[450px]">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
        <AlertTriangle
          size={16}
          className="mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400"
        />
        <p>
          <span className="font-medium">{formatPlatformList(affected)}</span>{' '}
          {isPlural ? 'connections have' : 'connection has'} expired and may not be able to publish.{' '}
          <Link to="/account/social-media" className="font-medium underline underline-offset-2">
            Reconnect in settings
          </Link>
        </p>
      </div>
    </div>
  )
}
